import type { Conversation, ConversationMember } from '../types';

export const normalizeGroupMembers = (
  members: ConversationMember[] = [],
): ConversationMember[] =>
  members.map(({ id: memberId, name: memberName, avatar, isAdmin, locked }) => ({
    id: memberId,
    name: memberName,
    avatar,
    isAdmin,
    locked,
  }));

export const buildGroupDisplayName = (
  groupName?: string,
  members: ConversationMember[] = [],
): string => {
  const trimmed = groupName?.trim();
  if (trimmed) return trimmed;
  return members
    .map((m) => (m?.name ? m.name.split(' ')[0] : ''))
    .filter(Boolean)
    .join(', ');
};

/** Tiles rendered in a group collage avatar before the rest are dropped. */
export const GROUP_COLLAGE_MAX_TILES = 4;

/**
 * Members to tile in a group collage avatar. `recentSenderIds` is expected
 * newest-first (the order MessageContext keeps its messages in), so the most
 * recently active distinct senders lead and the remaining members backfill.
 */
export const selectCollageMembers = (
  members: ConversationMember[] = [],
  recentSenderIds: Array<string | number> = [],
): ConversationMember[] => {
  const valid = members.filter(Boolean);
  if (valid.length <= 1 || recentSenderIds.length === 0) {
    return valid.slice(0, GROUP_COLLAGE_MAX_TILES);
  }

  const byId = new Map(valid.map((member) => [String(member.id), member]));
  const picked: ConversationMember[] = [];
  const seen = new Set<string>();

  for (const senderId of recentSenderIds) {
    const key = String(senderId);
    if (seen.has(key)) continue;
    const member = byId.get(key);
    if (!member) continue;
    seen.add(key);
    picked.push(member);
    if (picked.length === GROUP_COLLAGE_MAX_TILES) return picked;
  }

  for (const member of valid) {
    if (seen.has(String(member.id))) continue;
    picked.push(member);
    if (picked.length === GROUP_COLLAGE_MAX_TILES) break;
  }

  return picked;
};

/** True when a conversation has a dedicated (non-collage) group photo URL. */
export const hasUsableGroupAvatar = (avatar: unknown): boolean => {
  if (typeof avatar === 'string') return avatar.trim().length > 0;
  if (avatar && typeof avatar === 'object' && 'uri' in avatar) {
    const uri = (avatar as { uri?: string }).uri;
    return typeof uri === 'string' && uri.trim().length > 0;
  }
  return false;
};

/**
 * Groups that need member data for the collage avatar: no custom photo and
 * no members available yet (list payloads often omit participants).
 */
export const needsGroupCollageHydration = (
  conversation: Conversation,
): boolean => {
  if (conversation.category !== 'groups') return false;
  if (hasUsableGroupAvatar(conversation.avatar)) return false;
  return !(conversation.members && conversation.members.length > 0);
};
